import type { AgentStepEvent } from './types';

/**
 * Agent kit — the NDJSON transport, both ends.
 *
 * `EventSource` is not usable for these flows: it is GET-only, and every agent
 * run happens inline with the POST that requested it. Reading the response body
 * directly keeps each flow to one endpoint and one round trip.
 *
 * NOTE — deliberately NO `'use client'` directive.
 *
 * This module holds both halves of the transport: `streamAgentRun` is called
 * from client components, `ndjsonAgentResponse` from server route handlers.
 * Marking it `'use client'` would place a server-only helper inside a client
 * module and have four route handlers import across that boundary.
 *
 * No directive is needed. There are no hooks and no JSX here — just `fetch` and
 * a `Response` builder, both of which exist in either environment. The client
 * components that consume it carry their own `'use client'`, which is where the
 * boundary actually belongs.
 */
export async function streamAgentRun<TKey extends string, TResult>(
  endpoint: string,
  payload: Record<string, unknown>,
  handlers: {
    onEvent: (event: AgentStepEvent<TKey, TResult>) => void;
    signal?: AbortSignal;
  },
): Promise<TResult> {
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal: handlers.signal,
    body: JSON.stringify({ stream: true, ...payload }),
  });

  if (!response.ok || !response.body) {
    const errorPayload = await response.json().catch(() => null);
    throw new Error(errorPayload?.error || 'Request failed');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: TResult | null = null;
  let failure: string | null = null;

  const consume = (line: string) => {
    let event: AgentStepEvent<TKey, TResult>;
    try {
      event = JSON.parse(line) as AgentStepEvent<TKey, TResult>;
    } catch {
      // One malformed line is not worth failing an otherwise good run over.
      return;
    }
    handlers.onEvent(event);
    if (event.type === 'done') result = event.result;
    if (event.type === 'error') failure = event.message;
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // A chunk boundary can split a line, so the trailing partial waits in the
    // buffer until its newline arrives.
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    lines.map((line) => line.trim()).filter(Boolean).forEach(consume);
  }
  if (buffer.trim()) consume(buffer.trim());

  if (failure) throw new Error(failure);
  if (result === null) throw new Error('Run ended without returning a result');
  return result;
}

/**
 * Server-side helper: wrap an agent run in an NDJSON `Response`.
 *
 * Keeps the framing identical across every flow's route, so a route only has to
 * supply the run itself.
 */
export function ndjsonAgentResponse<TKey extends string, TResult>(
  run: (emit: (event: AgentStepEvent<TKey, TResult>) => void) => Promise<TResult>,
): Response {
  const encoder = new TextEncoder();
  const stream = new ReadableStream({
    async start(controller) {
      const write = (event: AgentStepEvent<TKey, TResult>) => {
        controller.enqueue(encoder.encode(`${JSON.stringify(event)}\n`));
      };
      try {
        const result = await run(write);
        write({ type: 'done', result });
      } catch (error) {
        console.error('Agent stream failed:', error);
        write({
          type: 'error',
          message: error instanceof Error ? error.message : 'Run failed',
        });
      } finally {
        controller.close();
      }
    },
  });

  return new Response(stream, {
    headers: {
      'Content-Type': 'application/x-ndjson; charset=utf-8',
      'Cache-Control': 'no-cache, no-transform',
      Connection: 'keep-alive',
    },
  });
}
